<script setup lang="ts">
/**
 * Renders a captured email body.
 *
 * The content is attacker-influenced: an email body is assembled from templates and customer data, so
 * it must never execute in the portal's origin. Three things enforce that:
 *
 *  - `sandbox` with **no** tokens. Not `allow-scripts`, so inline script and event handlers are inert,
 *    and not `allow-same-origin`, so the document cannot reach portal cookies, storage or the DOM.
 *    An empty value is the restrictive one; omitting the attribute entirely disables sandboxing.
 *  - `srcdoc` rather than assigning `innerHTML` anywhere, so the markup is never parsed as part of the
 *    portal document.
 *  - the frame is only populated when `active`, so switching to a sibling tab constructs nothing.
 *
 * Remote images load normally, which is a deliberate trade: it renders the mail as the recipient saw
 * it, at the cost of tripping any tracking pixel the message carries when an operator opens this tab.
 */
import { computed } from 'vue'

const props = defineProps<{
  html: string

  /**
   * Whether this preview is the visible tab. `TabPanel` keeps every tab mounted, so without this the
   * frame would be built for a body nobody is looking at.
   */
  active?: boolean
}>()

const srcdoc = computed(() => (props.active ? props.html : ''))
</script>

<template>
  <div class="html-preview">
    <p class="notice">Scripts are disabled. Remote images load.</p>

    <iframe
      class="frame"
      sandbox=""
      referrerpolicy="no-referrer"
      title="Rendered email body"
      :srcdoc="srcdoc"
    ></iframe>
  </div>
</template>

<style scoped>
.html-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
}

.notice {
  margin: 0;
  font-size: 11px;
  color: hsl(var(--color-40));
}

/* White regardless of theme: an email body carries its own colours and assumes a light client, so
   inheriting a dark portal background would render dark-on-dark text. */
.frame {
  width: 100%;
  height: 420px;
  border: 1px solid hsl(var(--color-80));
  border-radius: 4px;
  background-color: white;
}
</style>
